import { useState, useEffect, useCallback } from 'react';
import { progressApi, EnhancedParticipantStats, RecentActivity } from '@/api/progress';
import { useAuth } from '@/contexts/AuthContext';
import { refineErrorMessage } from '@/utils/errorMessages';

export interface ChallengeProgressData {
  totalDistance: number;
  distanceGoal: number;
  averagePace: string | null;
  bestDay: { date: string; distance: number } | null;
  activeDays: number;
  totalActiveDays: number;
  currentDay: number;
  daysLeft: number;
  recentActivities: RecentActivity[];
  // Challenge context
  challengeName: string;
  challengeType: string; // 'run' | 'marathon' | etc.
  challengeStatus: string; // 'pending' | 'active' | 'completed'
  totalDays: number;
  stakeAmount: number;
  totalPrizePool: number;
  userStakePaid: number;
  // Marathon-specific fields
  totalDuration: number; // Total run duration in seconds
  targetDurationSeconds: number | null; // Target time for marathon (null for regular challenges)
  isOffChain: boolean; // True for marathon challenges
}

interface UseChallengeProgressResult {
  data: ChallengeProgressData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch challenge progress data for a participant.
 *
 * This hook always attempts to fetch real data from the API.
 * If the API call fails (user not a participant, challenge not found, etc.),
 * it returns an error state - no mock data fallback.
 *
 * The routing logic should ensure this hook is only used on pages
 * where the user is a confirmed participant (e.g., /my-challenge/:id).
 */
export function useChallengeProgress(challengeId: string | undefined): UseChallengeProgressResult {
  const { user } = useAuth();

  const [data, setData] = useState<ChallengeProgressData | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    // Validate required params
    if (!challengeId) {
      setError('Challenge ID is required');
      return;
    }

    if (!user?.walletAddress) {
      setError('Please connect your wallet');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response: EnhancedParticipantStats = await progressApi.getEnhancedStats(
        challengeId,
        user.walletAddress
      );

      setData({
        totalDistance: response.totalRunDistance,
        distanceGoal: response.challengeContext.totalDistanceGoal,
        averagePace: response.averagePace,
        bestDay: response.bestDay,
        activeDays: response.daysCompleted,
        totalActiveDays: response.challengeContext.currentDay,
        currentDay: response.challengeContext.currentDay,
        daysLeft: response.challengeContext.daysLeft,
        recentActivities: response.recentActivities,
        // Challenge context
        challengeName: response.challengeContext.name,
        challengeType: response.challengeContext.type,
        challengeStatus: response.challengeContext.status,
        totalDays: response.challengeContext.durationDays,
        stakeAmount: response.challengeContext.stakeAmount,
        totalPrizePool: response.challengeContext.totalPrizePool,
        userStakePaid: response.challengeContext.userStakePaid,
        // Marathon-specific fields
        totalDuration: response.totalRunDuration,
        targetDurationSeconds: response.challengeContext.targetDurationSeconds,
        isOffChain: response.challengeContext.isOffChain,
      });
    } catch (err: any) {
      setError(refineErrorMessage(err));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [challengeId, user?.walletAddress]);

  // Fetch on mount or when dependencies change
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchStats,
  };
}
